/**
 * TanStack Query owns server truth (spec §14.7).
 *
 *   "strategy document staleTime 0, models/APIs 60 s, credentials 5 min,
 *    preflight 0; mutations never optimistic for save/run/credentials."
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api-client.js";

export const STALE = {
  /** The document a user is editing: always revalidate. */
  strategy: 0,
  preflight: 0,
  strategies: 5_000,
  me: 30_000,
  models: 60_000,
  apis: 60_000,
  credentials: 300_000,
  /** Settings reads: fresh enough to be true, quiet enough not to poll. */
  plan: 60_000,
  riskDefaults: 30_000,
  agents: 10_000,
  disclosures: 60_000,
  twoFactor: 60_000,
} as const;

export const queryKeys = {
  me: ["me"] as const,
  strategies: ["strategies"] as const,
  strategy: (id: string) => ["strategy", id] as const,
  runs: (strategyId?: string) => ["runs", strategyId ?? "all"] as const,
  run: (id: string) => ["run", id] as const,
  runHistory: (id: string) => ["run-history", id] as const,
  plan: ["plan"] as const,
  riskDefaults: ["risk-defaults"] as const,
  credentials: ["credentials"] as const,
  /** §12.6 API definitions: the catalogue, and one definition. */
  apis: (strategyId?: string) => ["apis", strategyId ?? "all"] as const,
  api: (id: string) => ["api", id] as const,
  /** §10 models, their versions and the datasets they train on. */
  models: (strategyId?: string) => ["models", strategyId ?? "all"] as const,
  model: (id: string) => ["model", id] as const,
  modelVersion: (id: string, version: number) => ["model-version", id, version] as const,
  modelTestPredictions: (id: string, version: number) =>
    ["model-test-predictions", id, version] as const,
  datasets: (strategyId?: string) => ["datasets", strategyId ?? "all"] as const,
  dataset: (id: string) => ["dataset", id] as const,
  /** The §10.2 venue-depth table. A research result, so it is nearly static. */
  venueCoverage: ["venue-coverage"] as const,
  agents: ["agents"] as const,
  disclosures: ["disclosures"] as const,
  twoFactor: ["two-factor"] as const,
};

/** Retrying a 401 or a 404 only delays the redirect the router is about to do. */
function retry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status !== 0 && error.status < 500) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry,
        staleTime: STALE.strategies,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}
